import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart"
import {
  emvCentsToDollars,
  formatEmvCompact,
  formatEmvUsd,
} from "@/lib/format-emv"
import { cn } from "@/lib/utils"
import type { EmvProgressionPoint } from "@/types/emv-analytics"

/**
 * OKS-503 — Brand EMV progression chart (cumulative area, single series).
 *
 * Structural sibling of `AnalyticsTrendChart` (OKS-63): single series
 * (`cumulative_cents`) plotted as an area, `--chart-1` token, same a11y
 * posture (`role="img"` + summary `aria-label` + sibling `sr-only`
 * table mirroring each day's cumulative + incremental EMV). The tooltip
 * shows the day's cumulative total AND its day-over-day incremental gain
 * in full USD.
 *
 * Money arrives as integer cents; the chart plots dollar magnitudes
 * (`cents/100`) so the Y axis reads in USD. Empty-state: when the series
 * is empty or flat-zero, the body renders the empty card variant — the
 * card surface itself never collapses (layout stability).
 */
export interface EmvProgressionChartProps {
  progression: EmvProgressionPoint[]
  /** Range total for the aria summary (integer cents). */
  totalCents: number
  className?: string
}

export function EmvProgressionChart({
  progression,
  totalCents,
  className,
}: EmvProgressionChartProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language

  const config: ChartConfig = useMemo(
    () => ({
      cumulative: {
        label: t("emv.chart.title"),
        theme: { light: "var(--chart-1)", dark: "var(--chart-1)" },
      },
    }),
    [t],
  )

  // Plot dollar magnitudes; keep the raw cents on each datum for the
  // tooltip (exact USD) + sr-only mirror.
  const data = useMemo(
    () =>
      progression.map((p) => ({
        date: p.date,
        cumulative: emvCentsToDollars(p.cumulative_cents),
        cumulative_cents: p.cumulative_cents,
        incremental_cents: p.incremental_cents,
      })),
    [progression],
  )

  const isEmpty =
    progression.length === 0 ||
    progression.every((p) => p.cumulative_cents === 0)

  return (
    <Card
      data-testid="emv-progression-chart"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("emv.chart.title")}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          <ChartBody
            config={config}
            data={data}
            progression={progression}
            totalCents={totalCents}
            locale={locale}
          />
        )}
        <SrOnlyTable progression={progression} locale={locale} />
      </CardContent>
    </Card>
  )
}

interface ChartDatum {
  date: string
  cumulative: number
  cumulative_cents: number
  incremental_cents: number
}

interface ChartBodyProps {
  config: ChartConfig
  data: ChartDatum[]
  progression: EmvProgressionPoint[]
  totalCents: number
  locale: string
}

function ChartBody({
  config,
  data,
  progression,
  totalCents,
  locale,
}: ChartBodyProps) {
  const { t } = useTranslation()
  const ariaSummary = t("emv.chart.aria_summary", {
    start: progression[0]?.date,
    end_date: progression[progression.length - 1]?.date,
    total: formatEmvUsd(totalCents, locale),
  })

  return (
    <div
      data-testid="emv-progression-chart-canvas"
      role="img"
      aria-label={ariaSummary}
    >
      <ChartContainer
        config={config}
        className="aspect-auto h-[200px] w-full sm:h-[240px] lg:h-[260px]"
      >
        <AreaChart
          data={data}
          margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={24}
            tickFormatter={(value: string) =>
              new Date(value).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })
            }
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={48}
            tickFormatter={(value: number) =>
              formatEmvCompact(Math.round(value * 100), locale)
            }
          />
          <ChartTooltip content={<EmvTooltip locale={locale} />} />
          <Area
            type="monotone"
            dataKey="cumulative"
            stroke="var(--color-cumulative)"
            fill="var(--color-cumulative)"
            fillOpacity={0.15}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ChartContainer>
    </div>
  )
}

/**
 * Custom tooltip — shows the hovered day's cumulative total + that day's
 * incremental gain, both in full USD.
 */
function EmvTooltip({
  active,
  payload,
  locale,
}: {
  active?: boolean
  payload?: { payload: ChartDatum }[]
  locale: string
}) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null

  const datum = payload[0].payload
  return (
    <div className="bg-background grid gap-1 rounded-lg border px-3 py-2 text-xs shadow-md">
      <span className="text-foreground font-medium">{datum.date}</span>
      <span className="text-muted-foreground">
        {t("emv.chart.mirror_table.cumulative")}:{" "}
        <span className="text-foreground font-medium">
          {formatEmvUsd(datum.cumulative_cents, locale)}
        </span>
      </span>
      <span className="text-muted-foreground">
        {t("emv.chart.mirror_table.incremental")}:{" "}
        <span className="text-foreground font-medium">
          {formatEmvUsd(datum.incremental_cents, locale)}
        </span>
      </span>
    </div>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="emv-progression-chart-empty"
      className="text-muted-foreground flex h-[200px] flex-col items-center justify-center gap-2 text-sm sm:h-[240px] lg:h-[260px]"
    >
      <p className="text-foreground text-sm font-medium">
        {t("emv.chart.empty.title")}
      </p>
      <p className="max-w-md text-center">{t("emv.chart.empty.body")}</p>
    </div>
  )
}

/**
 * Sr-only mirror table — one row per day (cumulative + incremental).
 * Screen readers consume this directly; also a stable Playwright
 * selector (canvas testing is fragile).
 */
function SrOnlyTable({
  progression,
  locale,
}: {
  progression: EmvProgressionPoint[]
  locale: string
}) {
  const { t } = useTranslation()
  return (
    <table className="sr-only" data-testid="emv-progression-chart-mirror-table">
      <caption>{t("emv.chart.mirror_table_caption")}</caption>
      <thead>
        <tr>
          <th>{t("emv.chart.mirror_table.date")}</th>
          <th>{t("emv.chart.mirror_table.cumulative")}</th>
          <th>{t("emv.chart.mirror_table.incremental")}</th>
        </tr>
      </thead>
      <tbody>
        {progression.map((p) => (
          <tr key={p.date}>
            <td>{p.date}</td>
            <td>{formatEmvUsd(p.cumulative_cents, locale)}</td>
            <td>{formatEmvUsd(p.incremental_cents, locale)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
